import store from "store2";

export function timestampToDate(ts) {
  ts = ts.split(".")[0];
  return new Date(ts * 1000);
}

export function getCurrentTimestamp() {
  const utc = Date.now();

  const sec = Math.floor(utc / 1e3);
  const ns = 1e3 * (utc - sec * 1e3);
  return sec.toString() + "." + ns.toString().padEnd(6, "0");
}

export function timestampToString(ts) {
  try {
    return timestampToDate(ts).toISOString();
  } catch (e) {
    return "";
  }
}

export function getUiProp(item, key, index, defaultValue = 1) {
  if (item && item.st_ui && key in item.st_ui) {
    return item.st_ui[key][index.toString()] || defaultValue;
  }

  return defaultValue;
}

export function getImageSizeFactor(item, index) {
  const key = `f${index}`;

  if (item && item.st_ui && item.st_ui[key]) {
    return item.st_ui[key] / 100;
  }

  return 1;
}

export function isSlackMessage(item) {
  return "event_ts" in item || item.st_source === "slack";
}

export function detectBrowser() {
  let userAgent = navigator.userAgent;
  let browserName;
  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "edge";
  } else {
    browserName = "unknown";
  }

  return browserName;
}

export function equalArrays(lhs, rhs) {
  if ((!lhs || !lhs.length) && (!rhs || !rhs.length)) {
    return true;
  }

  if (!lhs || !rhs || lhs.length !== rhs.length) {
    return false;
  }

  // this is stupid i know
  for (const v of lhs) {
    if (!rhs.includes(v)) {
      return false;
    }
  }

  for (const v of rhs) {
    if (!lhs.includes(v)) {
      return false;
    }
  }
  return true;
}

export function timePeriodToDays(period) {
  if (!period || period.length < 2) {
    return -1;
  }

  const unit = period[period.length - 1];
  const val = Number(period.slice(0, -1));
  if (unit == "d") {
    return val;
  } else if (unit == "w") {
    return 7 * val;
  } else if (unit == "m") {
    return 30 * val;
  } else if (unit == "y") {
    return 365 * val;
  }

  return -1;
}

export const getExtFromUrl = (url) => {
  const u = new URL(url);
  const ext = u.pathname.split(".").slice(-1)[0];
  return ext.length <= 4 ? ext : "";
};

export const getExtFromUrlLower = (url) => {
  return getExtFromUrl(url).toLocaleLowerCase();
};

export const getExt = (image) => {
  if (image.type) {
    let ext = image.type.split("/").slice(-1)[0];
    if (ext) {
      return ext.toLowerCase();
    }
  }

  const url = image.url;

  // this is weird because we have to keep this in sync with the server
  // and s3 is case sensitive and this splitting ans slicing is exactly what backend is doing
  if (!url || typeof url !== "string") {
    return "";
  }

  return getExtFromUrl(url).toLowerCase();
};

export async function sha1(str) {
  const buffer = new TextEncoder("utf-8").encode(str);
  const hash = await crypto.subtle.digest("SHA-1", buffer);
  const hexCodes = [];
  const view = new DataView(hash);
  for (let i = 0; i < view.byteLength; i += 1) {
    const byte = view.getUint8(i).toString(16).padStart(2, "0");
    hexCodes.push(byte);
  }
  return hexCodes.join("");
}

export const getStoryKey = (item) => {
  let key = item.SK4;
  return `STORY-${Number(key)}`;
};

export const getStoryUrlFromId = (storyId, project) => {
  const url =
    window.location.protocol +
    "//" +
    window.location.host +
    "/story/" +
    project +
    "/" +
    encodeURIComponent(storyId) +
    "/" +
    window.location.search;

  return url;
};

export const getStoryUrl = (item, project) => {
  let storyId = item.SK;
  if ("SK4" in item) {
    storyId = `${Number(item.SK4)}`;
  }
  return getStoryUrlFromId(storyId, project);
};

export const sortMessages = (messages, order) => {
  if (!messages) {
    return messages;
  }

  let sorted = Array.from(messages);
  sorted.sort((a, b) => {
    const lt = order ? -1 : 1;
    const ka = a.st_edit ? a.st_edit + a.SK : a.SK;
    const kb = b.st_edit ? b.st_edit + b.SK : b.SK;

    if (ka < kb) {
      return lt;
    } else if (ka === kb) {
      return 0;
    } else {
      return -lt;
    }
  });
  return sorted;
};

export const projectFromLocation = () => {
  const path = window.location.pathname;
  const parts = path.split("/");
  if (parts.length == 2) {
    return parts[1];
  }

  if (path.startsWith("/project/")) {
    return parts[2];
  }

  return "";
};

export const generateCacheKey = (key, project = "") => {
  const proj = projectFromLocation() || project || "";

  const locpath = window.location.pathname;
  let path =
    locpath == "/" || locpath === `/project/${proj}` || locpath === `/${proj}`
      ? ""
      : locpath;
  return `${key}:${proj}:${path}:${window.location.search}`;
};

export const getHomeUrl = () => {
  const proto = window.location.protocol;
  const host = window.location.host;
  const query = window.location.search;
  const url = `${proto}//${host}${query}`;

  return url;
};

export const getProjectUrl = (project) => {
  const proto = window.location.protocol;
  const host = window.location.host;
  const query = window.location.search;
  const url = `${proto}//${host}/${project}${query}`;

  return url;
};

export const parsePythonTimestamp = (ts) => {
  const ms = Math.floor(parseFloat(ts) * 1000);
  return new Date(ms);
};

export const todayRange = () => {
  let from = new Date();
  from.setHours(0);
  from.setMinutes(0);
  from.setSeconds(0);
  from.setMilliseconds(0);

  let to = new Date();
  to.setHours(23);
  to.setMinutes(59);
  to.setSeconds(59);
  to.setMilliseconds(999);

  return [from, to];
};

export const isTodayDate = (date) => {
  const now = new Date();
  return (
    now.getFullYear() == date.getFullYear() &&
    now.getMonth() == date.getMonth() &&
    now.getDate() == date.getDate()
  );
};

export const writeLocalStorage = (key, value) => {
  const qualifiedKey = `${key}:${window.location.pathname}${window.location.search}`;
  store(qualifiedKey, value);
};

export const readLocalStorage = (key, defaultValue = null) => {
  const qualifiedKey = `${key}:${window.location.pathname}${window.location.search}`;
  return store.get(qualifiedKey, defaultValue);
};

export const trimNewLine = (str) => {
  let start = 0;
  while (start < str.length && (str[start] == "\n" || str[start] == "\r")) {
    start += 1;
  }

  if (start == str.length) {
    return "\n";
  }

  let end = str.length - 1;
  while (end > start && (str[end] == "\n" || str[end] == "\r")) {
    end -= 1;
  }

  end += 1;

  const len = end - start;
  if (len == str.length) {
    return str;
  }

  return str.slice(start, end);
};
